import { useCallback, useMemo, useState } from 'react';
import {
  Alert, Button, Chip, Dialog, DialogActions, DialogContent, DialogTitle,
  Divider, Grid, InputAdornment, Skeleton, Stack, Table, TableBody, TableCell,
  TableHead, TableRow, TextField, Typography,
} from '@mui/material';
import type { CashDeskShift, CashDeskShiftTransaction } from '../../api/types';
import {
  useAmendShiftClosing, useCashDeskShiftTransactions, useMyPermissions,
} from '../../api/queries';
import { fmtCurrencyCents, fmtDate, fmtDateTime, fmtOrEmDash } from '../../utils/format';
import { exportShiftDetailPdf } from '../../utils/export';
import type { ShiftDetailLine } from '../../utils/export';

/** Colour for a variance figure: zero neutral, short red, over amber. */
export function varianceColor(varianceCents: number | null): string {
  if (varianceCents === null || varianceCents === 0) return 'text.primary';
  return varianceCents < 0 ? 'error.main' : 'warning.main';
}

/**
 * Merge the raw desk records into the single-line statement the owner
 * specified (2026-08-23): every AFT-on transfer is ONE line (money_received
 * + transfer_result + any resolution collapse by txId), the open/close
 * lines carry their amounts, a variance line sits under the close, and the
 * whole list runs latest-first.
 */
export function buildShiftLines(
  shift: CashDeskShift, txs: CashDeskShiftTransaction[],
): ShiftDetailLine[] {
  type Group = { records: CashDeskShiftTransaction[] };
  const transferGroups = new Map<string, Group>();
  const lines: ShiftDetailLine[] = [];

  for (const t of txs) {
    if ((t.kind === 'money_received' || t.kind === 'transfer_result' || t.kind === 'transfer_resolved') && t.txId) {
      const g = transferGroups.get(t.txId) ?? { records: [] };
      g.records.push(t);
      transferGroups.set(t.txId, g);
      continue;
    }
    if (t.kind === 'shift_opened') {
      lines.push({ at: t.occurredAt, event: 'Shift opened', machine: null,
        amountCents: shift.openingCents, outcome: null, order: 0 });
    } else if (t.kind === 'shift_closed') {
      lines.push({ at: t.occurredAt, event: 'Shift closed — counted', machine: null,
        amountCents: shift.originalClosingCents ?? shift.closingCents, outcome: null, order: 0 });
    } else if (t.kind === 'cashout_paid') {
      lines.push({
        at: t.occurredAt,
        event: t.status === 'gift_card' ? 'Cashout — gift-card load' : 'Cashout paid — cash',
        machine: t.machineName ? `${t.positionNo !== null ? `${t.positionNo} · ` : ''}${t.machineName}` : null,
        amountCents: t.amountCents !== null ? -t.amountCents : null,
        outcome: t.status === 'gift_card' ? (t.reference ?? 'gift card') : 'paid',
        order: 0,
      });
    } else if (t.kind === 'terminal_local_revoke') {
      lines.push({ at: t.occurredAt, event: 'Terminal revoked (local)', machine: null,
        amountCents: null, outcome: null, order: 0 });
    }
  }

  for (const [, g] of transferGroups) {
    const received = g.records.find((r) => r.kind === 'money_received');
    const result = g.records.find((r) => r.kind === 'transfer_result');
    const resolved = g.records.find((r) => r.kind === 'transfer_resolved');
    const anchor = received ?? result ?? g.records[0];
    const final = resolved ?? result;
    const outcome = final?.status === 'completed' ? 'completed'
      : final?.status === 'failed' ? `failed${final.failureReason ? ` (${final.failureReason})` : ''}`
      : final?.status === 'timeout_unknown' ? 'unresolved'
      : 'in progress';
    lines.push({
      at: anchor.occurredAt,
      event: 'Transfer to machine',
      machine: anchor.machineName
        ? `${anchor.positionNo !== null ? `${anchor.positionNo} · ` : ''}${anchor.machineName}`
        : null,
      amountCents: anchor.amountCents,
      outcome,
      order: 0,
    });
  }

  // Latest first; the variance line rides directly UNDER the close line.
  lines.sort((a, b) => (a.at === b.at ? 0 : a.at < b.at ? 1 : -1));
  if (shift.status === 'closed' && shift.varianceCents !== null) {
    const closeIdx = lines.findIndex((l) => l.event.startsWith('Shift closed'));
    const varianceLine: ShiftDetailLine = {
      at: shift.closedAt ?? '', event: 'Variance', machine: null,
      amountCents: shift.varianceCents, outcome: null, order: 1, emphasis: true,
    };
    if (closeIdx >= 0) lines.splice(closeIdx + 1, 0, varianceLine);
    else lines.unshift(varianceLine);
    if (shift.amendedAt && shift.originalClosingCents !== null) {
      lines.splice(closeIdx >= 0 ? closeIdx + 1 : 0, 0, {
        at: shift.amendedAt, event: 'Counted cash amended (RM)', machine: null,
        amountCents: shift.closingCents, outcome: shift.amendNote ?? shift.amendedByName ?? 'amended',
        order: 0, emphasis: true,
      });
    }
  }
  return lines;
}

export function ShiftDetailDialog({ shift, siteLabel, onClose }: {
  shift: CashDeskShift | null;
  /** "Site display name" (+ operator) for the PDF header. */
  siteLabel?: string;
  onClose: () => void;
}) {
  const tx = useCashDeskShiftTransactions(shift?.shiftId ?? null);
  const perms = useMyPermissions();
  const canAmend = perms.data?.codes?.includes('cashdesk.manager') ?? false;
  const amend = useAmendShiftClosing(shift?.siteId ?? null);

  const [amendOpen, setAmendOpen] = useState(false);
  const [amendDollars, setAmendDollars] = useState('');
  const [amendNote, setAmendNote] = useState('');
  // Optimistic overlay so the dialog reflects the amendment immediately
  // (the row prop refreshes when the grid re-fetches).
  const [overlay, setOverlay] = useState<Partial<CashDeskShift> | null>(null);
  const view = useMemo(
    () => (shift ? { ...shift, ...(overlay ?? {}) } : null),
    [shift, overlay]);

  const lines = useMemo(
    () => (view && tx.data ? buildShiftLines(view, tx.data) : []),
    [view, tx.data]);

  const amendCents = Math.round(Number(amendDollars) * 100);
  const amendValid = Number.isFinite(amendCents) && amendCents >= 0 && amendDollars !== '';

  const downloadPdf = useCallback(() => {
    if (!view) return;
    void exportShiftDetailPdf(view, lines, siteLabel ?? '');
  }, [view, lines, siteLabel]);

  return (
    <Dialog open={shift !== null} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        <Stack direction="row" spacing={1.5} alignItems="center">
          <span>
            Shift {view?.shiftNo !== null && view?.shiftNo !== undefined ? `#${view.shiftNo}` : ''} — {fmtOrEmDash(view?.terminalName ?? view?.deskCode)}
          </span>
          {view && (
            <Chip size="small" label={view.status}
                  color={view.status === 'open' ? 'success' : 'default'} />
          )}
          {view?.amendedAt && (
            <Chip size="small" label="amended" color="warning" data-testid="amended-chip" />
          )}
          <span style={{ flex: 1 }} />
          <Button size="small" onClick={downloadPdf} data-testid="shift-pdf">Export PDF</Button>
          {canAmend && view?.status === 'closed' && (
            <Button size="small" variant="outlined" data-testid="shift-amend"
              onClick={() => {
                setAmendDollars(view.closingCents !== null ? (view.closingCents / 100).toString() : '');
                setAmendNote('');
                setAmendOpen(true);
              }}>
              Amend counted cash
            </Button>
          )}
        </Stack>
      </DialogTitle>
      <DialogContent>
        {view && (
          <Stack spacing={2}>
            <Grid container spacing={2}>
              <Figure label="Cashier"   value={view.cashierName} />
              <Figure label="Terminal"  value={fmtOrEmDash(view.terminalName)} />
              <Figure label="Gaming day" value={fmtDate(view.gamingDay)} />
              <Figure label="Opened"    value={fmtDateTime(view.openedAt)} />
              <Figure label="Closed"    value={view.closedAt ? fmtDateTime(view.closedAt) : '—'} />
              <Figure label="Opening cash" value={fmtCurrencyCents(view.openingCents)} />
              <Figure label="Expected cash"
                      value={view.expectedCents !== null ? fmtCurrencyCents(view.expectedCents) : '—'} />
              <Figure label="Counted cash"
                      value={view.closingCents !== null ? fmtCurrencyCents(view.closingCents) : '—'} />
              <Figure label="Variance"
                      value={view.varianceCents !== null ? fmtCurrencyCents(view.varianceCents) : '—'}
                      color={varianceColor(view.varianceCents)} />
              <Figure label="Transfers"
                      value={`${view.transferCount} · ${fmtCurrencyCents(view.transferTotalCents)}`} />
            </Grid>

            {view.amendedAt && (
              <Alert severity="warning" data-testid="amended-banner">
                Counted cash amended {fmtDateTime(view.amendedAt)}
                {view.amendedByName ? ` by ${view.amendedByName}` : ''} — original figure{' '}
                {view.originalClosingCents !== null ? fmtCurrencyCents(view.originalClosingCents) : '—'}
                {view.amendNote ? `. Note: ${view.amendNote}` : ''}
              </Alert>
            )}

            <Divider />
            <Typography variant="overline" color="text.secondary">
              Transactions (latest first)
            </Typography>

            {tx.isLoading && <Skeleton variant="rectangular" height={120} />}
            {tx.isError && (
              <Typography variant="body2" color="error">Failed to load transactions.</Typography>
            )}
            {tx.data && (
              <Table size="small" data-testid="shift-transactions">
                <TableHead>
                  <TableRow>
                    <TableCell>When</TableCell>
                    <TableCell>Event</TableCell>
                    <TableCell>Machine</TableCell>
                    <TableCell align="right">Amount</TableCell>
                    <TableCell>Result</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {lines.map((l, i) => (
                    <TableRow key={i} sx={l.emphasis ? { '& td': { fontWeight: 700 } } : undefined}>
                      <TableCell>{l.at ? fmtDateTime(l.at) : '—'}</TableCell>
                      <TableCell>{l.event}</TableCell>
                      <TableCell>{l.machine ?? '—'}</TableCell>
                      <TableCell align="right" sx={{ fontVariantNumeric: 'tabular-nums' }}>
                        {l.amountCents !== null ? fmtCurrencyCents(l.amountCents) : '—'}
                      </TableCell>
                      <TableCell>{l.outcome ?? '—'}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </Stack>
        )}
      </DialogContent>

      {/* Manager amendment (RM-only, audited; the SC journal stays immutable). */}
      <Dialog open={amendOpen} onClose={() => setAmendOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Amend counted cash</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <Typography variant="body2" color="text.secondary">
              Corrects the counted figure for this closed shift — the original
              figure is retained and the change is audited against your user.
            </Typography>
            <TextField label="Counted cash" autoFocus value={amendDollars}
              onChange={(e) => setAmendDollars(e.target.value.replace(/[^\d.]/g, ''))}
              InputProps={{ startAdornment: <InputAdornment position="start">$</InputAdornment> }}
              inputProps={{ 'data-testid': 'amend-amount', inputMode: 'decimal' }} />
            <TextField label="Reason / note" value={amendNote} multiline minRows={2}
              onChange={(e) => setAmendNote(e.target.value)}
              inputProps={{ 'data-testid': 'amend-note' }} />
            {amend.isError && (
              <Alert severity="error">
                {(amend.error as { message?: string })?.message ?? 'Amendment failed.'}
              </Alert>
            )}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAmendOpen(false)}>Cancel</Button>
          <Button variant="contained" disabled={!amendValid || amend.isPending}
            data-testid="amend-save"
            onClick={() => {
              if (!view) return;
              amend.mutate(
                { shiftId: view.shiftId, closingCents: amendCents, note: amendNote.trim() || null },
                {
                  onSuccess: (r) => {
                    setOverlay({
                      closingCents: r.closingCents,
                      varianceCents: r.varianceCents,
                      originalClosingCents: r.originalClosingCents,
                      amendedAt: new Date().toISOString(),
                      amendNote: amendNote.trim() || null,
                    });
                    setAmendOpen(false);
                  },
                });
            }}>
            Save amendment
          </Button>
        </DialogActions>
      </Dialog>
    </Dialog>
  );
}

function Figure({ label, value, color }: { label: string; value: string; color?: string }) {
  return (
    <Grid item xs={6} md={2.4}>
      <Typography variant="caption" color="text.secondary" display="block">{label}</Typography>
      <Typography variant="body2" sx={{ fontVariantNumeric: 'tabular-nums' }} color={color}>
        {value}
      </Typography>
    </Grid>
  );
}
